import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Stack,
} from '@mui/material';
import { client } from '../../services/api/familypal/config';

const REFERRAL_STORAGE_KEY = 'familypal_referral_code';

interface LookupResult {
  referrer_first_name?: string;
  valid?: boolean;
}

export const InvitePage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referrerName, setReferrerName] = useState<string | null>(null);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    if (!code) {
      setLoading(false);
      return;
    }

    // Store the referral code
    localStorage.setItem(REFERRAL_STORAGE_KEY, code);

    // Look up the referral code
    client.get({
      url: '/familypal/v1/identity/referral/lookup/{code}',
      path: { code },
    }).then((response: any) => {
      const data = response.data as LookupResult | undefined;
      if (data?.valid) {
        setValid(true);
        setReferrerName(data.referrer_first_name || null);
      }
    }).catch(() => {
      // Invalid code, still show generic page
    }).finally(() => {
      setLoading(false);
    });
  }, [code]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F3F4F6' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F3F4F6', py: 4 }}>
      <Container maxWidth="sm">
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={700} color="primary" gutterBottom>
            FamilyPal
          </Typography>

          {valid && referrerName ? (
            <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
              {referrerName} invited you to FamilyPal!
            </Typography>
          ) : (
            <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
              You've been invited to FamilyPal!
            </Typography>
          )}

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            The easiest way to organize your family's life. Shared calendars, messaging, school tracking, and more.
          </Typography>

          <Stack spacing={2}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/signup"
              sx={{ py: 1.5 }}
            >
              Sign Up Free
            </Button>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                size="large"
                href="https://apps.apple.com/app/familypal"
                target="_blank"
                rel="noopener"
              >
                App Store
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="https://play.google.com/store/apps/details?id=com.familypal"
                target="_blank"
                rel="noopener"
              >
                Google Play
              </Button>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
            Already have an account?{' '}
            <RouterLink to="/login" style={{ color: 'inherit' }}>Sign in</RouterLink>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default InvitePage;
